/**
 * OfflineBanner — shows "No connection" banner when offline.
 * Also shows pending sync count and "Back online" toast.
 */
import { useState, useEffect, useRef } from "react";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { getPendingQueueCounts } from "../lib/syncQueue";

export function OfflineBanner() {
  const { isOnline, wasOffline } = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [showReconnected, setShowReconnected] = useState(false);
  const wasOfflineRef = useRef(false);

  // Track pending queue items
  useEffect(() => {
    if (!isOnline) {
      const interval = setInterval(async () => {
        const counts = await getPendingQueueCounts();
        setPendingCount(counts.audioUploads + counts.tripReports);
      }, 5000);
      // Initial check
      getPendingQueueCounts().then((counts) => {
        setPendingCount(counts.audioUploads + counts.tripReports);
      });
      return () => clearInterval(interval);
    } else {
      setPendingCount(0);
    }
  }, [isOnline]);

  // Show "Back online" toast when reconnecting
  useEffect(() => {
    if (isOnline && wasOfflineRef.current) {
      setShowReconnected(true);
      const timer = setTimeout(() => setShowReconnected(false), 3000);
      return () => clearTimeout(timer);
    }
    wasOfflineRef.current = !isOnline;
  }, [isOnline]);

  if (showReconnected && wasOffline) {
    return (
      <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-center bg-success px-4 py-2 text-sm font-medium text-white transition-all duration-300">
        <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        Back online — syncing data...
      </div>
    );
  }

  if (isOnline) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-center bg-error px-4 py-2 text-sm font-medium text-white">
      <svg className="mr-2 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
      </svg>
      <span>No connection</span>
      {pendingCount > 0 && (
        <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
          {pendingCount} pending
        </span>
      )}
    </div>
  );
}
